import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { IStore } from 'redux/IStore';
import { RouteComponentProps } from 'react-router';
import { getShop } from 'redux/modules/shop/manageshop';
import moment = require('moment');
import { ErrorSummary, PersonItem } from 'components';
import { IOffice, IOfficeTime } from 'models/shop/office';
import { ITranslateProps } from 'models/common';
import { ShopAppointmentsFilterHeader } from '../ShopAppointments/FilterHeader';
import { getOffice } from '../ShopAppointments/helpers';
import { deleteBookingTimetableGroup, setBookingTimetablesFiltersOffice } from 'redux/modules/shop/bookingtimetables';
import { dayMonthFormatter, formatDuration, shouldComponentUpdate } from 'helpers/common';
import { formatWorkTime, normalizeBookingTimetablesMultipleResources } from 'helpers/bookingTemplates';
import { closeModal, showModal } from 'redux/modules/modal';
import { getBookingTemplatesByOffice } from 'redux/modules/shop/bookingtemplates';
import { NoBookingTemplates, NoOfficeTimes, NoStaffMembers } from '../Common/BookingTemplatesProblems';
import { IBookingTimeTableGroup } from 'models/shop/bookingtimetables';
import { IConfirmationDialogProps } from 'models/modal';
import { LoadingOverlayWrapper } from 'components/Loader';
import { BasicButtonGrey } from 'components/Common/Button';
import {
	getOfficeTimeForDay,
	IEditDayWorkTimeOwnProps,
	IEditWeekWorkTimeOwnProps,
	IEditWorkTimeBaseProps,
} from './helpers';
import { IOfficeMember } from 'models/shop/members';
import { isAdmin, momentWithTimezone } from 'redux/modules/shop/helpers';

interface IRouterParams {
	id: string;
	code: string;
	officeId: string;
}

interface IOwnProps extends RouteComponentProps<IRouterParams, {}> {}

type IProps = IOwnProps & ReturnType<typeof stateToProps> & ReturnType<typeof dispatchToProps> & ITranslateProps;

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class OfficeWorkTimeComponent extends React.Component<IProps> {
	constructor(props) {
		super(props);

		this.changeOfficeId = this.changeOfficeId.bind(this);
		this.editWeek = this.editWeek.bind(this);
		this.editDay = this.editDay.bind(this);
		this.renderStaffMemberSchedule = this.renderStaffMemberSchedule.bind(this);
	}

	private fetchEverything(resetShop = false, officeDiff?) {
		let membersOfficeId = undefined;
		const role = (this.props.userPreferences?.userRoles || []).find(
			(role) =>
				role.role === 'Member' && role?.type == 'Office' && role?.objInfo?.attributes?.ShopId === this.props.params?.id,
		);
		if (role) {
			membersOfficeId = role.objId;
		}

		if (resetShop || (!this.props.offices && !this.props.isFetchingShop)) {
			this.props.getShop(this.props.params.id);
			this.props.getOfficeServices(this.props.params.officeId);
		} else if (this.props.offices?.length && !this.props.params.officeId) {
			const officeId =
				membersOfficeId || this.props.offices.find((off) => off.isDefault).id || this.props.offices[0].id;
			this.changeOfficeId(officeId);
		}
		if (this.props.office && this.props.staff && !this.props.isFetchingTimetables) {
			this.props.getTimetablesForOffice(this.props.office.id);
		}
		if (officeDiff) {
			this.props.getOfficeServices(this.props.params.officeId);
		}
	}

	private changeOfficeId(officeId: string, push = false) {
		if (this.props.params.officeId !== officeId) {
			const routerFn = this.props.router[push ? 'push' : 'replace'];
			routerFn('/shop/' + this.props.params.id + '/worktime/' + officeId);
		}
	}

	public shouldComponentUpdate(nextProps: Readonly<IProps>): boolean {
		return shouldComponentUpdate(this.props, nextProps);
	}

	public componentDidMount() {
		this.fetchEverything(true);
	}

	public componentDidUpdate(prevProps: IProps): void {
		const { offices, office, bookingTimetables, params } = this.props;

		if (prevProps.params.id !== params.id) {
			return this.fetchEverything(true);
		}
		if (!offices || !bookingTimetables) {
			return this.fetchEverything(false);
		}
		if (params.officeId !== prevProps.params.officeId) {
			this.fetchEverything(false, true); // officeDiff
		}
		if (office && (!prevProps.office || office.id !== prevProps.office.id)) {
			this.fetchEverything(false);
		}
		if (!params.officeId) {
			this.fetchEverything();
		}
	}

	private get staffIds(): string[] {
		return this.props.staff.map((s) => s.id);
	}

	private buildEditParams(resource: IOfficeMember): IEditWorkTimeBaseProps {
		const timezone = this.props.office?.zoneInfo;
		return {
			monday: momentWithTimezone(timezone, this.props.date).startOf('day'),
			resource,
			office: this.props.office,
			timetables: this.normalizedTimetables[resource.id] || [],
			partiallyUnavailableTimetables: this.partiallyUnavailableTimetables[resource.id] || [],
			staffIds: this.staffIds,
		};
	}

	private editWeek(resource: IOfficeMember) {
		const resourceId = resource.id;
		if (!resourceId || !this.props.office) {
			return;
		}
		this.props.editWeek(this.buildEditParams(resource));
	}

	private editDay(resource: IOfficeMember, day: number) {
		const resourceId = resource.id;
		if (!resourceId || !this.props.office) {
			return;
		}
		this.props.editDay({
			...this.buildEditParams(resource),
			day,
		});
	}

	private getOfficeTimeForDay(date: moment.Moment): IOfficeTime | undefined {
		const { office } = this.props;
		return getOfficeTimeForDay(office, date);
	}

	private get normalizedTimetables(): { [resourceId: string]: IBookingTimeTableGroup[] } {
		const { bookingTimetables, date, office } = this.props;
		if (!office) {
			return {};
		}
		const timezone = office?.zoneInfo;
		const minDate = momentWithTimezone(timezone, date).startOf('days');
		const maxDate = minDate.clone().add(7, 'day');
		const dateLimitedTimetables = bookingTimetables.filter((tt) => {
			const startTime = momentWithTimezone(timezone, tt.startTime);
			const endTime = momentWithTimezone(timezone, tt.endTime);
			if (!startTime.isSame(endTime, 'days')) {
				return false;
			}
			return startTime.isBetween(minDate, maxDate, undefined, '[)');
		});

		// return available timetables
		const { available } = normalizeBookingTimetablesMultipleResources(dateLimitedTimetables, timezone);
		return available;
	}

	private get partiallyUnavailableTimetables(): { [resourceId: string]: IBookingTimeTableGroup[] } {
		const { bookingTimetables, partiallyUnavailableTimetables, date, office } = this.props;

		if (!office) {
			return {};
		}

		const timezone = office?.zoneInfo;
		const minDate = momentWithTimezone(timezone, date).startOf('days');
		const maxDate = minDate.clone().add(7, 'day');
		const dateLimitedTimetables = bookingTimetables.filter((tt) => {
			const startTime = momentWithTimezone(timezone, tt.startTime);
			const endTime = momentWithTimezone(timezone, tt.endTime);
			if (!startTime.isSame(endTime, 'days')) {
				return false;
			}
			return startTime.isBetween(minDate, maxDate, undefined, '[)');
		});

		dateLimitedTimetables.push(...partiallyUnavailableTimetables);

		// return partially unavailable timetables
		const { partial } = normalizeBookingTimetablesMultipleResources(dateLimitedTimetables, timezone);
		return partial;
	}

	private get isFetching(): boolean {
		return this.props.fetchingBookingTemplates || this.props.isFetchingShop || this.props.isFetchingTimetables;
	}

	private renderStaffMemberSchedule(member: IOfficeMember, i: number): JSX.Element {
		const { t } = this.props;
		const timezone = this.props.office?.zoneInfo;
		const currentDay = momentWithTimezone(timezone, this.props.date).startOf('day');
		const timetableGroups = this.normalizedTimetables[member.id] || [];

		const getSumTimeSeconds = (date?: moment.Moment) =>
			timetableGroups.reduce((acc, val) => {
				var isSame = !momentWithTimezone(timezone, val.startTime).isSame(date, 'day');
				if (date && isSame) {
					return acc;
				}
				// moment.tz is probably not needed here (maybe if times are on different days when converted to utc?)
				const duration = moment.duration(moment(val.endTime).diff(moment(val.startTime)));
				return acc + duration.asSeconds();
			}, 0);

		const formatSumTime = (date?: moment.Moment): JSX.Element => {
			const s = getSumTimeSeconds(date);
			if (s) {
				return <span>{formatDuration(s, true)}</span>;
			} else {
				return <span className="muted">00:00</span>;
			}
		};
		const groupsForDate = (date: moment.Moment): IBookingTimeTableGroup[] =>
			timetableGroups.filter((tt) => momentWithTimezone(timezone, tt.startTime).isSame(date, 'day'));
		const workWeekDays: JSX.Element[] = [];

		for (let i = 0; i < 7; i++) {
			const sum = getSumTimeSeconds(currentDay);
			workWeekDays.push(
				<div className="workWeekDay" key={i} onClick={() => this.editDay(member, i)}>
					{groupsForDate(currentDay).map((g) => (
						<div className="period" key={g.startTime}>
							{momentWithTimezone(timezone, g.startTime).format('H:mm')} -{' '}
							{momentWithTimezone(timezone, g.endTime).format('H:mm')}
						</div>
					))}
					{sum ? <span className="sum">{formatSumTime(currentDay)}</span> : null}
				</div>,
			);
			currentDay.add(1, 'day');
		}

		return (
			<React.Fragment key={member.id || member.id || i}>
				<PersonItem user={member} vertical={true} />
				<div className="workTimeSum">
					{formatSumTime()}
					<div className="muted label">{t('Periodselector_Periodicity_1')}</div>
					<BasicButtonGrey onClick={() => this.editWeek(member)}>
						{this.props.t('BSPortal_Common_Edit')}
					</BasicButtonGrey>
				</div>
				{workWeekDays}
			</React.Fragment>
		);
	}

	public render() {
		const { date, params, staff, isFetchingShop, t, userPreferences } = this.props;
		const headerItems: JSX.Element[] = [];
		const salonTimeItems: JSX.Element[] = [];
		const timezone = this.props.office?.zoneInfo;
		for (let i = 0; i < 7; i++) {
			const mom = momentWithTimezone(timezone, date).add(i, 'days');
			headerItems.push(
				<div key={i}>
					<strong>{mom.format('dd ')}</strong>
					{dayMonthFormatter(mom)}
				</div>,
			);
			const wt = this.getOfficeTimeForDay(mom);
			if (wt) {
				salonTimeItems.push(<div key={i}>{formatWorkTime(wt, timezone)}</div>);
			} else {
				salonTimeItems.push(
					<div key={i} className="muted">
						{t('Shop_ManageShop_Location_Label_OpeningTimes_Closed')}
					</div>,
				);
			}
		}

		const isSiteAdmin: boolean = userPreferences.isAdministrator;
		const isShopOrOfficeAdmin: boolean =
			isAdmin(params.id, userPreferences.userRoles) || isAdmin(params.officeId, userPreferences.userRoles);

		let schedule: JSX.Element;
		const noOfficeTimes = this.props.office && !this.props.office.officeTimes?.length;
		if (this.props.bookingTemplates?.length && !noOfficeTimes && staff?.length) {
			schedule = (
				<LoadingOverlayWrapper active={this.isFetching} className="scheduleHolder summary">
					<div className="topCoordinate">
						<div className="muted label">{t('Shop_WorkTime_Label_Days')}</div>
						{headerItems}
					</div>
					<div className="topCoordinate dark">
						<div className="muted label">{t('Shop_WorkTime_Label_OpeningTime')}</div>
						{salonTimeItems}
					</div>
					<div className="scheduleTableHolder">
						<div className="scheduleTable workTimeSummary">{staff.map(this.renderStaffMemberSchedule)}</div>
					</div>
				</LoadingOverlayWrapper>
			);
		} else if (noOfficeTimes) {
			schedule = <NoOfficeTimes shopId={params.id} hideButton={!isSiteAdmin && !isShopOrOfficeAdmin} />;
		} else if (!staff?.length && !isFetchingShop) {
			schedule = (
				<NoStaffMembers
					shopId={params.id}
					officeId={params.officeId}
					hideButton={!isSiteAdmin && !isShopOrOfficeAdmin}
				/>
			);
		} else if (!this.props.fetchingBookingTemplates) {
			schedule = (
				<NoBookingTemplates
					shopId={params.id}
					officeId={params.officeId}
					hideButton={!isSiteAdmin && !isShopOrOfficeAdmin}
				/>
			);
		}
		return (
			<div className="topPadding shopAppointments">
				<ErrorSummary error={this.props.error} message={this.props.message} />
				<ShopAppointmentsFilterHeader
					section="bookingTimetables"
					officeId={params.officeId}
					changeOfficeId={this.changeOfficeId}
					timezone={timezone}
				/>
				{schedule}
			</div>
		);
	}
}

const stateToProps = (state: IStore, ownProps: IOwnProps) => {
	const bookingTemplates = (state.shopBookingTemplates.services || []).filter(
		(bt) => bt.officeId === ownProps.params.officeId,
	);
	const bookingTemplatesIds = bookingTemplates.map((bt) => bt.id);
	const bookingTimetables = (state.bookingTimetables.items || []).filter((btt) =>
		bookingTemplatesIds.includes(btt.bookingTemplateId),
	);
	// TASK 8507/8509 Partially unavailable and unavailable time tables no longer have booking template id's on them
	const partiallyUnavailableTimetables = (state.bookingTimetables.items || []).filter(
		(btt) => JSON.parse(btt.entityId).type === 'PartiallyUnavailableTimeTable',
	);

	return {
		offices: state.manageShop?.shop?.offices,
		date: state.bookingTimetables.date,
		staff: getOffice(state, ownProps.params.officeId)?.staff,
		isFetchingShop: state.manageShop.isFetching || state.manageShop.error,
		fetchingBookingTemplates: state.shopBookingTemplates.isFetching,
		bookingTemplates,
		bookingTimetables,
		partiallyUnavailableTimetables,
		error: state.bookingTimetables.error,
		message: state.bookingTimetables.message,
		isFetchingTimetables: state.bookingTimetables.isFetching,
		filterMemberId: state.bookingTimetables.memberId,
		office: getOffice(state, ownProps.params.officeId),
		userPreferences: state.userPreferences,
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	getShop: (id: string) => {
		dispatch(getShop(id, false, true));
	},
	getOfficeServices: (officeId: string) => {
		dispatch(getBookingTemplatesByOffice(officeId, true));
	},
	deleteTimetable: (group: IBookingTimeTableGroup) => dispatch(deleteBookingTimetableGroup(group)),
	getTimetablesForOffice: (officeId: IOffice['id']) => dispatch(setBookingTimetablesFiltersOffice(officeId)),
	editWeek: (modalProps: IEditWeekWorkTimeOwnProps) => dispatch(showModal('EditWeekWorkTime', modalProps)),
	editDay: (modalProps: IEditDayWorkTimeOwnProps) => dispatch(showModal('EditDayWorkTime', modalProps)),
	showConfirmationDialog: (modalProps: IConfirmationDialogProps) =>
		dispatch(showModal('ConfirmationDialog', modalProps)),
	closeModal: () => dispatch(closeModal()),
});

export const OfficeWorkTime = connect(stateToProps, dispatchToProps)(OfficeWorkTimeComponent);
